import api from './api';

// Toggle flag to control API vs mock data usage
// PRODUCTION: Set this to false when API is ready
export const USE_MOCK_DATA = false;

// User profile data types
export interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  gender: string;
}

// Address types
export interface Address {
  id: number;
  title: string;
  fullAddress: string;
  isDefault: boolean;
  city: string;
  street: string;
  house: string;
  apartment: string;
  zipCode: string;
}

// Order types
export interface OrderItem {
  id: number;
  name: string;
  quantity: number;
  price: number;
  image: string;
}

export interface Order {
  id: string;
  // backend may provide a human-friendly order_id and a numeric pk
  order_id?: string;
  orderId?: string;
  pk?: number;
  date: string; // created_at
  total: number;
  status: "processing" | "shipped" | "delivered" | "cancelled";
  items: OrderItem[];
  // optional extras from backend
  items_count?: number;
  first_item?: any;
  shippingAddress?: string;
  deliveryType?: string;
  trackingNumber?: string;
  payment?: any;
}

// Loyalty types
export interface LoyaltyData {
  points: number;
  history: LoyaltyHistoryItem[];
}

export interface LoyaltyHistoryItem {
  date: string;
  points: number;
  description: string;
}

// Review types
export interface Review {
  id: number;
  productId: number;
  productName: string;
  rating: number;
  text: string;
  date: string;
  productImage: string;
}

// Wishlist types
export interface WishlistItem {
  id: number;
  name: string;
  category: string;
  price: number;
  image: string;
}

// Recommendation types
export interface Recommendation {
  id: number;
  name: string;
  category: string;
  price: number;
  image: string;
  reason: string;
}

// Promotion types
export interface Promotion {
  id: number;
  title: string;
  description: string;
  code: string;
  expiryDate: string;
  discount: number;
}

// Mock data
// PRODUCTION: This entire section would be commented out
export const mockProfileData: ProfileData = {
  firstName: "Анна",
  lastName: "Иванова",
  email: "anna@example.com",
  phone: "+7 (999) 123-45-67",
  birthDate: "1990-05-15",
  gender: "female"
};

// For addresses we prefer to show an empty list by default to avoid
// displaying development mock addresses in the UI. If you need sample
// addresses during development, enable them here temporarily.
export const mockAddresses: Address[] = [];

export const mockOrders: Order[] = [
  {
    id: "ORD-123456",
    date: "15.05.2023",
    total: 7800,
    status: "delivered",
    items: [
      { id: 1, name: "Vanilla & Amber", quantity: 1, price: 2900, image: "https://img.heroui.chat/image/furniture?w=200&h=200&u=candle2" },
      { id: 2, name: "Cedar & Sage", quantity: 2, price: 3400, image: "https://img.heroui.chat/image/furniture?w=200&h=200&u=diffuser1" }
    ]
  },
  {
    id: "ORD-123457",
    date: "02.06.2023",
    total: 5600,
    status: "shipped",
    items: [
      { id: 3, name: "Black Orchid", quantity: 1, price: 3200, image: "https://img.heroui.chat/image/furniture?w=200&h=200&u=candle3" },
      { id: 4, name: "Fresh Linen", quantity: 2, price: 1200, image: "https://img.heroui.chat/image/furniture?w=200&h=200&u=diffuser2" }
    ]
  },
  {
    id: "ORD-123458",
    date: "18.06.2023",
    total: 9400,
    status: "processing",
    items: [
      { id: 5, name: "Forest Mist", quantity: 2, price: 2800, image: "https://img.heroui.chat/image/furniture?w=200&h=200&u=candle4" },
      { id: 6, name: "Amber Wood", quantity: 1, price: 3800, image: "https://img.heroui.chat/image/furniture?w=200&h=200&u=diffuser4" }
    ]
  }
];

export const mockLoyaltyData: LoyaltyData = {
  points: 450,
  history: [
    { date: "15.05.2023", points: 78, description: "Начисление за заказ ORD-123456" },
    { date: "02.06.2023", points: 56, description: "Начисление за заказ ORD-123457" },
    { date: "10.06.2023", points: -30, description: "Списание за скидку" },
    { date: "18.06.2023", points: 94, description: "Начисление за заказ ORD-123458" },
    { date: "01.07.2023", points: 252, description: "Бонус за день рождения" }
  ]
};

export const mockWishlistItems: WishlistItem[] = [
  { id: 101, name: "Vanilla & Amber", category: "Свеча", price: 2900, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=candle2" },
  { id: 203, name: "Citrus Grove", category: "Диффузор", price: 3200, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=diffuser3" },
  { id: 304, name: "Rose Garden", category: "Саше", price: 1400, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=sachet4" }
];

export const mockRecommendations: Recommendation[] = [
  { 
    id: 105, 
    name: "Lavender Dreams", 
    category: "Свеча", 
    price: 2600, 
    image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=candle6",
    reason: "Вам может понравиться, так как вы приобретали свечи этой серии"
  },
  { 
    id: 402, 
    name: "Cinnamon Spice", 
    category: "Мелтсы", 
    price: 1600, 
    image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=melt2",
    reason: "Другие покупатели часто приобретают это вместе с Cedar & Sage"
  },
  { 
    id: 204, 
    name: "Amber Wood", 
    category: "Диффузор", 
    price: 3800, 
    image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=diffuser4",
    reason: "Новинка, которая соответствует вашим предпочтениям"
  }
];

export const mockPromotions: Promotion[] = [
  {
    id: 1,
    title: "День рождения",
    description: "Скидка 15% на любой заказ в течение недели до и после дня рождения",
    code: "BIRTHDAY15",
    expiryDate: "22.05.2023",
    discount: 15
  },
  {
    id: 2,
    title: "Лояльный клиент",
    description: "Скидка 10% на третий заказ",
    code: "LOYAL10",
    expiryDate: "31.12.2023",
    discount: 10
  }
];

export const mockReviews: Review[] = [
  {
    id: 1,
    productId: 1,
    productName: "Vanilla & Amber",
    rating: 5,
    text: "Прекрасный аромат, долго держится. Очень довольна покупкой!",
    date: "20.05.2023",
    productImage: "https://img.heroui.chat/image/furniture?w=200&h=200&u=candle2"
  },
  {
    id: 2,
    productId: 2,
    productName: "Cedar & Sage",
    rating: 4,
    text: "Хороший аромат, но хотелось бы, чтобы был немного интенсивнее.",
    date: "15.06.2023",
    productImage: "https://img.heroui.chat/image/furniture?w=200&h=200&u=diffuser1"
  }
];

const REVIEW_ENDPOINTS = ['/user/reviews/', '/api/user/reviews/'];

const runReviewRequest = async <T>(executor: (endpoint: string) => Promise<T>): Promise<T> => {
  let lastError: any = null;
  for (const endpoint of REVIEW_ENDPOINTS) {
    try {
      return await executor(endpoint);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status !== 404 && status !== 405) {
        throw error;
      }
      lastError = error;
    }
  }
  if (lastError) {
    throw lastError;
  }
  throw new Error('Review endpoint is not available');
};

// API functions
// Each function has dual implementation - mock data and real API
// PRODUCTION: Keep only the API calls and remove the mock data part

/**
 * Get user profile
 */
export const getUserProfile = async (): Promise<ProfileData> => {
  // Production: call real API
  const response = await api.get<ProfileData>('/users/profile/');
  return response.data;
};

/**
 * Update user profile
 */
export const updateUserProfile = async (data: ProfileData): Promise<ProfileData> => {
  const response = await api.put<ProfileData>('/users/profile/', data);
  return response.data;
};

/**
 * Change user password
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string,
  newPasswordConfirm?: string
): Promise<boolean> => {
  try {
    // Backend contract: POST /api/users/change-password/ with fields current_password, new_password, new_password_confirm
    const payload: any = {
      current_password: currentPassword,
      new_password: newPassword
    };
    if (newPasswordConfirm !== undefined) payload.new_password_confirm = newPasswordConfirm;

    const response = await api.post('/users/change-password/', payload);
    return response.status === 200;
  } catch (err: any) {
    // Rethrow with response data when available so caller can show field errors
    if (err?.response?.data) {
      const e = new Error('Validation error');
      // @ts-ignore attach details
      e.details = err.response.data;
      throw e;
    }
    throw err;
  }
};

/**
 * Get user addresses
 */
export const getUserAddresses = async (): Promise<Address[]> => {
  const response = await api.get('/users/addresses/');
  const data = response.data;
  // Backend may return a paginated object { count, next, previous, results: [] }
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  // If unknown shape, return empty list to indicate "no addresses"
  return [];
};

/**
 * Add a new address
 */
export const addAddress = async (address: Omit<Address, 'id'>): Promise<Address> => {
  const response = await api.post<Address>('/users/addresses/', address);
  return response.data;
};

/**
 * Update an existing address
 */
export const updateAddress = async (address: Address): Promise<Address> => {
  const response = await api.put<Address>(`/users/addresses/${address.id}/`, address);
  return response.data;
};

/**
 * Delete an address
 */
export const deleteAddress = async (addressId: number): Promise<boolean> => {
  await api.delete(`/users/addresses/${addressId}/`);
  return true;
};

/**
 * Set address as default
 */
export const setDefaultAddress = async (addressId: number): Promise<boolean> => {
  await api.put(`/users/addresses/${addressId}/default/`);
  return true;
};

/**
 * Get user orders
 */
export const getUserOrders = async (): Promise<Order[]> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock orders data');
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve([...mockOrders]), 300);
    });
  }

  // Production: call backend endpoint per contract: GET /api/orders/my/
  const response = await api.get('/orders/my/');
  const data = response.data;
  // backend returns paginated response { count, next, previous, results }
  const list = Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : []);

  // Map backend items to our Order shape
  const mapped: Order[] = list.map((o: any) => ({
    id: String(o.id ?? o.order_id ?? o.pk ?? ''),
    orderId: o.order_id ?? undefined,
    pk: o.id ?? undefined,
    date: o.created_at || o.createdAt || '',
    total: Number(o.total || 0),
    status: (o.status as any) || 'processing',
    items: o.first_item ? [
      {
        id: o.first_item.id || 0,
        name: o.first_item.name || o.first_item.title || 'Товар',
        quantity: o.items_count ? Math.max(1, Math.floor(o.items_count)) : 1,
        price: o.first_item.price || 0,
        image: o.first_item.image || ''
      }
    ] : [],
    items_count: o.items_count ?? o.itemsCount ?? undefined,
    first_item: o.first_item ?? undefined,
    shippingAddress: o.address || o.shipping_address || o.shippingAddress || undefined,
    deliveryType: o.delivery_type || o.deliveryType || undefined
  }));

  return mapped;
};

/**
 * Get single order details
 */
export const getOrderDetails = async (orderId: string): Promise<Order | null> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock order details');
    return new Promise((resolve) => {
      // Find order by ID
      const order = mockOrders.find(o => o.id === orderId) || null;
      
      // Simulate API delay
      setTimeout(() => resolve(order), 300);
    });
  }

  // Production: call backend endpoint per contract: GET /api/orders/my/<pk>/
  // Here orderId is expected to be the pk (numeric id). If UI passes order.orderId (human id),
  // the caller should be adjusted to pass pk. We'll try both: first by pk, then by order_id.
  try {
    const response = await api.get(`/orders/my/${orderId}/`);
    const o = response.data;
    if (!o) return null;

    const mapped: Order = {
      id: String(o.id ?? o.order_id ?? ''),
      orderId: o.order_id ?? undefined,
      pk: o.id ?? undefined,
      date: o.created_at || o.createdAt || '',
      total: Number(o.total || 0),
      status: (o.status as any) || 'processing',
      items: Array.isArray(o.items) ? o.items.map((it: any) => ({
        id: it.id,
        name: it.name || it.product_name || '',
        quantity: it.quantity || 1,
        price: it.price || it.unit_price || 0,
        image: it.image || it.product_image || ''
      })) : [],
      items_count: o.items_count ?? undefined,
      first_item: o.first_item ?? undefined,
      shippingAddress: o.address || o.shipping_address || o.shippingAddress || undefined,
      deliveryType: o.delivery_type || o.deliveryType || undefined,
      payment: o.payment ?? undefined,
      trackingNumber: o.tracking_number || o.trackingNumber || undefined
    };

    return mapped;
  } catch (err) {
    // If primary attempt fails and caller passed order.orderId, try to fetch by order_id path if available
    try {
      const response = await api.get(`/orders/my/${orderId}/`);
      const o = response.data;
      if (!o) return null;
      const mapped: Order = {
        id: String(o.id ?? o.order_id ?? ''),
        orderId: o.order_id ?? undefined,
        pk: o.id ?? undefined,
        date: o.created_at || o.createdAt || '',
        total: Number(o.total || 0),
        status: (o.status as any) || 'processing',
        items: Array.isArray(o.items) ? o.items.map((it: any) => ({
          id: it.id,
          name: it.name || it.product_name || '',
          quantity: it.quantity || 1,
          price: it.price || it.unit_price || 0,
          image: it.image || it.product_image || ''
        })) : [],
        items_count: o.items_count ?? undefined,
        first_item: o.first_item ?? undefined,
        shippingAddress: o.address || o.shipping_address || o.shippingAddress || undefined,
        deliveryType: o.delivery_type || o.deliveryType || undefined,
        payment: o.payment ?? undefined,
        trackingNumber: o.tracking_number || o.trackingNumber || undefined
      };

      return mapped;
    } catch (err2) {
      console.error('Error fetching order details:', err2);
      return null;
    }
  }
};

/**
 * Cancel an order
 */
export const cancelOrder = async (orderId: string): Promise<boolean> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock cancel order');
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve(true), 500);
    });
  }

  // PRODUCTION: Uncomment the following code and remove the mock data part above
  /*
  await api.put(`/api/user/orders/${orderId}/cancel`);
  return true;
  */
  
  return true;
};

/**
 * Get loyalty data
 */
export const getLoyaltyData = async (): Promise<LoyaltyData> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock loyalty data');
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve({ ...mockLoyaltyData }), 300);
    });
  }

  // PRODUCTION: Uncomment the following code and remove the mock data part above
  /*
  const response = await api.get<LoyaltyData>('/api/user/loyalty');
  return response.data;
  */
  
  return { ...mockLoyaltyData };
};

/**
 * Get user wishlist
 */
export const getWishlist = async (): Promise<WishlistItem[]> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock wishlist data');
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve([...mockWishlistItems]), 300);
    });
  }

  // PRODUCTION: Uncomment the following code and remove the mock data part above
  /*
  const response = await api.get<WishlistItem[]>('/api/user/wishlist');
  return response.data;
  */
  
  return [...mockWishlistItems];
};

/**
 * Add item to wishlist
 */
export const addToWishlist = async (productId: number): Promise<boolean> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock add to wishlist');
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve(true), 500);
    });
  }

  // PRODUCTION: Uncomment the following code and remove the mock data part above
  /*
  await api.post('/api/user/wishlist', { productId });
  return true;
  */
  
  return true;
};

/**
 * Remove item from wishlist
 */
export const removeFromWishlist = async (productId: number): Promise<boolean> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock remove from wishlist');
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve(true), 500);
    });
  }

  // PRODUCTION: Uncomment the following code and remove the mock data part above
  /*
  await api.delete(`/api/user/wishlist/${productId}`);
  return true;
  */
  
  return true;
};

/**
 * Get personal recommendations
 */
export const getRecommendations = async (): Promise<Recommendation[]> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock recommendations data');
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve([...mockRecommendations]), 300);
    });
  }

  // PRODUCTION: Uncomment the following code and remove the mock data part above
  /*
  const response = await api.get<Recommendation[]>('/api/user/recommendations');
  return response.data;
  */
  
  return [...mockRecommendations];
};

/**
 * Get personal promotions
 */
export const getPromotions = async (): Promise<Promotion[]> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock promotions data');
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve([...mockPromotions]), 300);
    });
  }

  // PRODUCTION: Uncomment the following code and remove the mock data part above
  /*
  const response = await api.get<Promotion[]>('/api/user/promotions');
  return response.data;
  */
  
  return [...mockPromotions];
};

/**
 * Get user reviews
 */
export const getUserReviews = async (): Promise<Review[]> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock reviews data');
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve([...mockReviews]), 300);
    });
  }

  const response = await runReviewRequest(async (endpoint) => api.get(endpoint));
  const data = response.data;
  if (Array.isArray(data)) return data as Review[];
  if (data && Array.isArray((data as any).results)) return (data as any).results as Review[];
  return [];
};

/**
 * Add a new review
 */
export const addReview = async (
  productId: number,
  rating: number,
  text: string
): Promise<Review> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock add review');
    // Dummy review for mock data
    const mockReview: Review = {
      id: Math.max(0, ...mockReviews.map(r => r.id)) + 1,
      productId,
      productName: "Новый товар",
      rating,
      text,
      date: new Date().toLocaleDateString('ru-RU'),
      productImage: "https://img.heroui.chat/image/furniture?w=200&h=200&u=candle2"
    };
    
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve(mockReview), 500);
    });
  }

  const response = await runReviewRequest((endpoint) =>
    api.post<Review>(endpoint, {
      productId,
      rating,
      text
    })
  );
  return response.data;
};

/**
 * Delete a review
 */
export const deleteReview = async (reviewId: number): Promise<boolean> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock delete review');
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve(true), 500);
    });
  }

  await runReviewRequest((endpoint) => api.delete(`${endpoint}${reviewId}/`));
  return true;
};

/**
 * Update a review
 */
export const updateReview = async (
  reviewId: number,
  rating: number,
  text: string
): Promise<Review> => {
  if (USE_MOCK_DATA) {
    // Using mock data
    console.log('Using mock update review');
    // Find the review to update
    const reviewToUpdate = mockReviews.find(r => r.id === reviewId);
    
    if (!reviewToUpdate) {
      throw new Error('Review not found');
    }
    
    const updatedReview = {
      ...reviewToUpdate,
      rating,
      text
    };
    
    return new Promise((resolve) => {
      // Simulate API delay
      setTimeout(() => resolve(updatedReview), 500);
    });
  }

  const response = await runReviewRequest((endpoint) =>
    api.put<Review>(`${endpoint}${reviewId}/`, {
      rating,
      text
    })
  );
  return response.data;
};
